import { AnimatePresence, motion } from "framer-motion"
import { RealTimeWrapper, Show } from 'components';

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-location';
import clsx from 'clsx';
import config from 'config';
import { usePendingPointRedemptionUsages } from 'hooks/data/use-point-redemptions';
import { usePlaceItOffers } from 'hooks/data/use-placeit-offers';
import { useState } from 'react';

type Props = {}

const RedemptionsFab = (props: Props) => {
    const [isFabOpen, setIsFabOpen] = useState(false);

    const {
        length: offerCount = 0,
        loading: offersLoading,
        refetch,
    } = usePlaceItOffers({
        brokerId: config.brokerId,
    });


    const {
        redemptions = [],
        loading: redemptionsLoading,
        refetch: refetchPointsRedemption,
    } = usePendingPointRedemptionUsages({
        brokerId: config.brokerId,
    });


    const displayOfferCount = offersLoading ? "..." : offerCount || 0;
    const shouldShowFab =
        (!offersLoading && offerCount > 0) ||
        (!redemptionsLoading && redemptions.length > 0);

    const refetchOnEvent = () => {
        refetch();
        refetchPointsRedemption();
    };

    const toggleFab = () => {
        setIsFabOpen(!isFabOpen);
    };

    return (
        <RealTimeWrapper
            channel={config.pusher.channel}
            events={[
                "cedant.manager_onboarded",
                "cedant.placed_risk",
                "cedant.updated_risk",
                "cedant.uploaded_supporting_documents",
                "cedant.send_thread_notification",
                "broker.send_thread_notification",
                "broker.update_offer",
                "broker.accept_offer",
                "broker.declined_offer",
            ]}
            callBack={refetchOnEvent}
        >
            <Show if={shouldShowFab}>
                <div className="fixed bottom-6 right-6 z-50">
                    <motion.button
                        onClick={toggleFab}
                        className={clsx(
                            "h-14 w-14 rounded-full shadow-lg flex items-center justify-center",
                            isFabOpen ? "bg-red-500" : "bg-blue-600"
                        )}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        animate={{
                            rotate: isFabOpen ? 45 : 0,
                            y: isFabOpen ? 0 : [0, -10, 0],
                        }}
                        transition={{
                            rotate: { type: "spring", stiffness: 300 },
                            y: {
                                repeat: isFabOpen ? 0 : Infinity,
                                duration: 1.5,
                                ease: "easeInOut",
                            },
                        }}
                    >
                        <InformationCircleIcon className="h-8 w-8 text-white" />
                    </motion.button>

                    <AnimatePresence>
                        {isFabOpen && (
                            <motion.div
                                className="absolute bottom-16 right-1 bg-white rounded-lg shadow-xl p-4 w-80"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 20,
                                }}
                            >
                                <div className="space-y-4">
                                    {!offersLoading && offerCount > 0 && (
                                        <Link to="/place-it-offers" className="block">
                                            <motion.div
                                                className="flex items-center hover:bg-gray-50 p-3 rounded transition-colors border border-gray-100"
                                                whileHover={{ x: 5 }}
                                            >
                                                <div className="flex-1">
                                                    <span className="text-gray-800 font-medium block mb-1">
                                                        PlaceIT Offers
                                                    </span>
                                                    <span className="text-gray-600 text-sm">
                                                        {displayOfferCount} offer
                                                        {displayOfferCount !== 1 ? "s" : ""} awaiting
                                                        action
                                                    </span>
                                                </div>
                                                <div className="ml-3 h-10 w-10 flex items-center justify-center rounded-full bg-secondary-600 text-white font-medium">
                                                    {displayOfferCount}
                                                </div>
                                            </motion.div>
                                        </Link>
                                    )}
                                    {!redemptionsLoading && redemptions.length > 0 && (
                                        <Link to="/points-redemptions" className="block">
                                            <motion.div
                                                className="flex items-center hover:bg-gray-50 p-3 rounded transition-colors border border-gray-100"
                                                whileHover={{ x: 5 }}
                                            >
                                                <div className="flex-1">
                                                    <span className="text-gray-800 font-medium block mb-1">
                                                        Redemptions
                                                    </span>
                                                    <span className="text-gray-600 text-sm">
                                                        {redemptions.length} coupon
                                                        {redemptions.length !== 1 ? "s" : ""} awaiting
                                                        action
                                                    </span>
                                                </div>
                                                <div className="ml-3 h-10 w-10 flex items-center justify-center rounded-full bg-primary-600 text-white font-medium">
                                                    {redemptions.length}
                                                </div>
                                            </motion.div>
                                        </Link>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </Show>
        </RealTimeWrapper>

    )
}

export default RedemptionsFab